/**
 * IndexedDB {@link AssetBackend} — the production backend for the content-addressed
 * asset store (Bombproof storage foundation, Layer 2).
 *
 * Uses a DEDICATED database (`stagecraft_assets`) separate from the document
 * store (`stagecraft_storage`), so asset lifecycle + future migrations never
 * interfere with the document tier. Values are the payload strings (data URLs);
 * a native-Blob encoding is a disclosed future optimization (approval-gate §8).
 *
 * NOT unit-tested against the non-persistent jsdom IDB mock (setupTests.ts) — the
 * persistent-IDB integration harness (fake-indexeddb) is the next infra step per
 * the handoff. The store LOGIC is unit-tested via InMemoryAssetBackend.
 */
import { AssetBackend } from "./assetStore";

const DB_NAME = "stagecraft_assets";
const STORE_NAME = "blobs";
const DB_VERSION = 1;

export class IndexedDbAssetBackend implements AssetBackend {
  private db: IDBDatabase | null = null;

  private async getDb(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // SG-W3: if ANOTHER tab upgrades the DB, close + clear this cached
        // connection so it neither blocks the upgrade nor serves a stale handle
        // (getDb re-opens on next use).
        db.onversionchange = () => {
          db.close();
          this.db = null;
        };
        this.db = db;
        resolve(db);
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async tx<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.getDb();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const request = run(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      // SG-W1: a transaction-level ABORT (e.g. quota exceeded, or a
      // versionchange forcing close) may not surface via the request's onerror
      // → the await would hang forever. Reject on abort/error too (whichever
      // settles first wins; a later reject on a settled promise is a no-op).
      transaction.onabort = () =>
        reject(
          transaction.error ?? new Error("IndexedDB transaction aborted"),
        );
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async get(id: string): Promise<string | null> {
    const result = await this.tx<string | undefined>("readonly", (s) => s.get(id));
    return result === undefined ? null : result;
  }

  async put(id: string, value: string): Promise<void> {
    await this.tx("readwrite", (s) => s.put(value, id));
  }

  async has(id: string): Promise<boolean> {
    // count(key) is 0/1 for a keyed store — avoids reading the (large) payload.
    const count = await this.tx<number>("readonly", (s) => s.count(id));
    return count > 0;
  }

  async keys(): Promise<string[]> {
    // getAllKeys() returns just the ids (never the large payloads). Reuses the
    // VERSIONED getDb()/tx() — never a versionless re-open (a versionless open
    // pre-empts onupgradeneeded and phantom-reports the store missing).
    const raw = await this.tx<IDBValidKey[]>("readonly", (s) => s.getAllKeys());
    return raw.filter((k): k is string => typeof k === "string");
  }

  async delete(id: string): Promise<void> {
    await this.tx("readwrite", (s) => s.delete(id));
  }
}
